/**
 * FastLane Variant Quantity Drawer
 *
 * Alibaba-style slide-out panel for selecting multiple variant quantities.
 * Extends the theme's DrawerComponent for consistent drawer behavior.
 */

class VariantQuantityDrawer extends DrawerComponent {
  constructor() {
    super();
    this.quantities = {};
    this.productData = null;
    this.selectedSelectorValue = null;
    this.filteredVariants = [];
    this.isSubmitting = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.loadProductData();
    this.bindTriggerButtons();
    this.bindSelectorButtons();
    this.bindFooterActions();
    this.bindProductFormSubmit();
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get moq() {
    return parseInt(this.dataset.moq) || 1;
  }

  get moqIncrement() {
    return parseInt(this.dataset.moqIncrement) || 1;
  }

  get selectorOptionIndex() {
    return parseInt(this.dataset.selectorOptionIndex);
  }

  get quantityOptionLabel() {
    if (!this.productData) return '';
    const opts = this.productData.options;
    if (this.selectorOptionIndex >= 0 && opts.length > 1) {
      for (let i = 0; i < opts.length; i++) {
        if (i !== this.selectorOptionIndex) return opts[i];
      }
    }
    return opts[0] || 'Option';
  }

  loadProductData() {
    const script = document.querySelector(`[data-vqd-product-data="${this.sectionId}"]`);
    if (!script) return;
    try {
      this.productData = JSON.parse(script.textContent);
    } catch (e) {
      console.error('[VQD] Failed to parse product data', e);
    }
  }

  bindTriggerButtons() {
    const triggerContainer = document.querySelector(
      `#ProductInfo-${this.sectionId} .vqd-trigger`
    );
    if (!triggerContainer) return;

    triggerContainer.querySelectorAll('.vqd-trigger__btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const optionIndex = parseInt(btn.dataset.optionIndex);
        const optionValue = btn.dataset.optionValue;

        btn.closest('.vqd-trigger__options')
          .querySelectorAll('.vqd-trigger__btn')
          .forEach((b) => b.classList.remove('vqd-trigger__btn--active'));
        btn.classList.add('vqd-trigger__btn--active');

        const selected = btn.closest('.vqd-trigger__group').querySelector('.vqd-trigger__selected');
        if (selected) selected.textContent = optionValue;

        if (this.selectorOptionIndex >= 0 && optionIndex === this.selectorOptionIndex) {
          this.selectSelectorValue(optionValue);
        }

        this.show(btn);
      });
    });
  }

  bindSelectorButtons() {
    this.querySelectorAll('.vqd-selectors__btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.querySelectorAll('.vqd-selectors__btn').forEach((b) =>
          b.classList.remove('vqd-selectors__btn--active')
        );
        btn.classList.add('vqd-selectors__btn--active');
        this.selectSelectorValue(btn.dataset.value);
      });
    });
  }

  /**
   * Intercept the main Add to Cart form submit to open the drawer instead.
   * Also overrides the PDP quantity input min to 1 since the drawer
   * handles total MOQ validation across all variant quantities.
   */
  bindProductFormSubmit() {
    // Only intercept when MOQ > 1. For no MOQ or MOQ of 1, let default Shopify add-to-cart work normally.
    if (this.moq <= 1) return;

    const productInfo = document.querySelector(`#ProductInfo-${this.sectionId}`);
    if (!productInfo) return;

    // Override quantity input min to allow quantity of 1 on PDP
    const qtyInput = productInfo.querySelector('.quantity__input');
    if (qtyInput) {
      qtyInput.min = '1';
      qtyInput.setAttribute('data-min', '1');
      qtyInput.value = '1';

      // Re-validate QuantityInput button states with new min
      const quantityInputEl = qtyInput.closest('quantity-input');
      if (quantityInputEl && quantityInputEl.validateQtyRules) {
        quantityInputEl.validateQtyRules();
      }
    }

    // Intercept form submit to open drawer instead of adding single variant
    const form = productInfo.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.show();
    }, true); // Capture phase to run before ProductForm's handler
  }

  bindFooterActions() {
    const addBtn = this.querySelector('[data-add-to-cart]');
    if (addBtn) {
      addBtn.addEventListener('click', () => this.addToCart());
    }

    const inquiryBtn = this.querySelector('[data-send-inquiry]');
    if (inquiryBtn) {
      inquiryBtn.addEventListener('click', () => this.sendInquiry());
    }
  }

  selectSelectorValue(value) {
    this.selectedSelectorValue = value;
    const label = this.querySelector('.vqd-selectors__selected');
    if (label) label.textContent = value;

    this.quantities = {};
    this.renderQuantityRows();
    this.updateTotals();

    // Update hero image to the first variant of the selected option
    const filtered = this.getFilteredVariants();
    if (filtered.length > 0) {
      const largeSrc = this.getVariantImageLarge(filtered[0]);
      if (largeSrc) {
        this.updateHeroImage(largeSrc, filtered[0].title || value);
      }
    }
  }

  prepareToShow() {
    super.prepareToShow();

    if (this.selectorOptionIndex >= 0 && !this.selectedSelectorValue) {
      const firstActive = this.querySelector('.vqd-selectors__btn--active');
      if (firstActive) {
        this.selectedSelectorValue = firstActive.dataset.value;
      } else {
        const first = this.querySelector('.vqd-selectors__btn');
        if (first) {
          first.classList.add('vqd-selectors__btn--active');
          this.selectedSelectorValue = first.dataset.value;
        }
      }
    }

    this.syncWithCart().then(() => {
      this.renderQuantityRows();
      this.updateTotals();
    });
  }

  async syncWithCart() {
    try {
      const resp = await fetch(`${this.dataset.cartUrl}.js`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      const cart = await resp.json();
      if (!cart || !cart.items) return;

      const variantIds = new Set(
        (this.productData?.variants || []).map((v) => v.id)
      );

      this.quantities = {};
      for (const item of cart.items) {
        if (variantIds.has(item.variant_id)) {
          this.quantities[item.variant_id] = item.quantity;
        }
      }
    } catch (e) {
      console.warn('[VQD] Could not sync with cart:', e);
    }
  }

  getFilteredVariants() {
    if (!this.productData) return [];
    const variants = this.productData.variants;

    if (this.selectorOptionIndex >= 0 && this.selectedSelectorValue) {
      const optKey = `option${this.selectorOptionIndex + 1}`;
      return variants.filter((v) => v[optKey] === this.selectedSelectorValue);
    }

    return variants;
  }

  getQuantityOptionIndex() {
    if (!this.productData) return 0;
    const opts = this.productData.options;
    if (this.selectorOptionIndex >= 0 && opts.length > 1) {
      for (let i = 0; i < opts.length; i++) {
        if (i !== this.selectorOptionIndex) return i;
      }
    }
    return 0;
  }

  getVariantImage(variant) {
    if (variant.featured_image) return variant.featured_image;
    if (this.productData?.product_image) return this.productData.product_image;
    return null;
  }

  getVariantImageLarge(variant) {
    if (variant.featured_image_large) return variant.featured_image_large;
    if (this.productData?.product_image_large) return this.productData.product_image_large;
    return null;
  }

  updateHeroImage(src, alt) {
    const heroImg = this.querySelector('[data-vqd-hero-img]');
    if (heroImg && src) {
      heroImg.src = src;
      heroImg.alt = alt || '';
    }
  }

  renderQuantityRows() {
    const container = this.querySelector('.vqd-quantities__list');
    const labelEl = this.querySelector('.vqd-quantities__label');
    if (!container) return;

    this.filteredVariants = this.getFilteredVariants();
    const qtyOptionIndex = this.getQuantityOptionIndex();
    const optionLabel = this.quantityOptionLabel;
    const showImages = this.productData?.show_variant_images !== false;

    if (labelEl) {
      labelEl.textContent = optionLabel;
    }

    const currencySymbol = this.productData?.currency_symbol || '$';

    container.innerHTML = this.filteredVariants
      .map((variant) => {
        const qty = this.quantities[variant.id] || 0;
        const optionValue = variant.options[qtyOptionIndex] || variant.title;
        const price = (variant.price / 100).toFixed(2);
        const isUnavailable = !variant.available;
        const imgSrc = showImages ? this.getVariantImage(variant) : null;

        const largeSrc = showImages ? this.getVariantImageLarge(variant) : null;

        return `
        <div class="vqd-row${isUnavailable ? ' vqd-row--unavailable' : ''}" data-variant-id="${variant.id}">
          ${
            imgSrc
              ? `<button type="button" class="vqd-row__image" data-hero-src="${largeSrc || ''}" data-hero-alt="${this.escapeHtml(optionValue)}" aria-label="View image for ${this.escapeHtml(optionValue)}"><img src="${imgSrc}" alt="${this.escapeHtml(optionValue)}" width="48" height="48" loading="lazy"></button>`
              : ''
          }
          <div class="vqd-row__info">
            <span class="vqd-row__value">${this.escapeHtml(optionValue)}</span>
            ${isUnavailable ? '<span class="vqd-row__unavailable">Unavailable</span>' : ''}
          </div>
          <div class="vqd-row__price">${currencySymbol}${price}</div>
          <div class="vqd-row__controls">
            <button type="button" class="vqd-row__btn vqd-row__btn--minus" data-action="minus" data-variant-id="${variant.id}" ${qty <= 0 || isUnavailable ? 'disabled' : ''} aria-label="Decrease quantity">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6h7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
            </button>
            <input
              type="number"
              class="vqd-row__input"
              value="${qty}"
              min="0"
              data-variant-id="${variant.id}"
              aria-label="Quantity for ${this.escapeHtml(optionValue)}"
              ${isUnavailable ? 'disabled' : ''}
            >
            <button type="button" class="vqd-row__btn vqd-row__btn--plus" data-action="plus" data-variant-id="${variant.id}" ${isUnavailable ? 'disabled' : ''} aria-label="Increase quantity">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 2.5v7M2.5 6h7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
            </button>
          </div>
        </div>`;
      })
      .join('');

    container.querySelectorAll('.vqd-row__image').forEach((imgBtn) => {
      imgBtn.addEventListener('click', () => {
        const src = imgBtn.dataset.heroSrc;
        const alt = imgBtn.dataset.heroAlt;
        if (src) this.updateHeroImage(src, alt);

        container.querySelectorAll('.vqd-row__image').forEach((b) =>
          b.classList.remove('vqd-row__image--active')
        );
        imgBtn.classList.add('vqd-row__image--active');
      });
    });

    container.querySelectorAll('.vqd-row__btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const variantId = parseInt(btn.dataset.variantId);
        const action = btn.dataset.action;
        this.adjustQuantity(variantId, action === 'plus' ? 1 : -1);
      });
    });

    container.querySelectorAll('.vqd-row__input').forEach((input) => {
      input.addEventListener('change', () => {
        const variantId = parseInt(input.dataset.variantId);
        const val = Math.max(0, parseInt(input.value) || 0);
        this.quantities[variantId] = val;
        input.value = val;
        this.updateRowState(variantId);
        this.updateTotals();
      });
    });
  }

  adjustQuantity(variantId, delta) {
    const current = this.quantities[variantId] || 0;
    const newQty = Math.max(0, current + delta);
    this.quantities[variantId] = newQty;

    const input = this.querySelector(`.vqd-row__input[data-variant-id="${variantId}"]`);
    if (input) input.value = newQty;

    this.updateRowState(variantId);
    this.updateTotals();
  }

  updateRowState(variantId) {
    const qty = this.quantities[variantId] || 0;
    const row = this.querySelector(`.vqd-row[data-variant-id="${variantId}"]`);
    if (!row) return;

    const minusBtn = row.querySelector('.vqd-row__btn--minus');
    if (minusBtn) minusBtn.disabled = qty <= 0;

    row.classList.toggle('vqd-row--selected', qty > 0);
  }

  getTotalQuantity() {
    return Object.values(this.quantities).reduce((sum, q) => sum + q, 0);
  }

  getSubtotal() {
    if (!this.productData) return 0;
    let total = 0;
    const tiers = this.productData.moq_tiers || [];
    const totalQty = this.getTotalQuantity();

    for (const [variantId, qty] of Object.entries(this.quantities)) {
      if (qty <= 0) continue;
      const variant = this.productData.variants.find((v) => v.id === parseInt(variantId));
      if (!variant) continue;

      let unitPrice = variant.price;

      if (tiers.length > 0) {
        for (let i = tiers.length - 1; i >= 0; i--) {
          if (totalQty >= tiers[i].min) {
            unitPrice = parseFloat(tiers[i].price) * 100;
            break;
          }
        }
      }

      total += unitPrice * qty;
    }
    return total;
  }

  updateTotals() {
    const totalQty = this.getTotalQuantity();
    const subtotal = this.getSubtotal();
    const currencySymbol = this.productData?.currency_symbol || '$';

    const qtyEl = this.querySelector('[data-total-qty]');
    if (qtyEl) qtyEl.textContent = totalQty;

    const subtotalEl = this.querySelector('[data-subtotal]');
    if (subtotalEl) subtotalEl.textContent = `${currencySymbol}${(subtotal / 100).toFixed(2)}`;

    const fillEl = this.querySelector('[data-moq-fill]');
    if (fillEl && this.moq > 1) {
      const pct = Math.min(100, (totalQty / this.moq) * 100);
      fillEl.style.width = `${pct}%`;
      fillEl.classList.toggle('vqd-moq-bar__fill--met', totalQty >= this.moq);
    }

    const addBtn = this.querySelector('[data-add-to-cart]');
    if (addBtn) {
      const meetsmoq = this.moq <= 1 || totalQty >= this.moq;
      const hasItems = totalQty > 0;
      addBtn.disabled = !meetsmoq || !hasItems || this.isSubmitting;

      const btnText = addBtn.querySelector('.btn__text');
      if (btnText) {
        if (!meetsmoq && totalQty > 0) {
          btnText.textContent = `Need ${this.moq - totalQty} more to meet MOQ`;
        } else if (hasItems) {
          btnText.textContent = `Add To Cart (${totalQty} pcs)`;
        } else {
          btnText.textContent = 'Add To Cart';
        }
      }
    }

    this.updateTierHighlight(totalQty);
  }

  updateTierHighlight(totalQty) {
    this.querySelectorAll('.vqd-tier').forEach((tier) => {
      tier.classList.remove('vqd-tier--active');
    });

    const tiers = Array.from(this.querySelectorAll('.vqd-tier'));
    for (let i = tiers.length - 1; i >= 0; i--) {
      const min = parseInt(tiers[i].dataset.tierMin);
      if (totalQty >= min) {
        tiers[i].classList.add('vqd-tier--active');
        break;
      }
    }
  }

  async addToCart() {
    const updates = {};
    const variantIds = new Set(
      (this.productData?.variants || []).map((v) => v.id)
    );

    // Build update map: set absolute quantities for all product variants
    for (const vid of variantIds) {
      const qty = this.quantities[vid] || 0;
      updates[vid] = qty;
    }

    const totalQty = Object.values(this.quantities).reduce(
      (s, q) => s + q,
      0
    );
    if (totalQty === 0) return;
    if (this.moq > 1 && totalQty < this.moq) return;

    this.setSubmitting(true);

    try {
      const response = await fetch(this.dataset.cartUpdateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Could not update cart');
      }

      const cartJson = await response.json();

      if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          cart: cartJson,
        });
      }

      this.hide();
      this.showCartDrawer();
    } catch (error) {
      console.error('[VQD] Add to cart failed:', error);
      this.showError(error.message);
    } finally {
      this.setSubmitting(false);
    }
  }

  showCartDrawer() {
    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { open: true },
      })
    );
  }

  showError(message) {
    let errorEl = this.querySelector('.vqd-error');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'vqd-error';
      this.querySelector('.vqd-footer').prepend(errorEl);
    }
    errorEl.textContent = message;
    errorEl.hidden = false;
    setTimeout(() => {
      errorEl.hidden = true;
    }, 5000);
  }

  setSubmitting(submitting) {
    this.isSubmitting = submitting;
    const addBtn = this.querySelector('[data-add-to-cart]');
    if (addBtn) {
      addBtn.classList.toggle('btn--loading', submitting);
      addBtn.disabled = submitting;
    }
  }

  sendInquiry() {
    const inquiryBtn = document.querySelector(
      `#ProductInfo-${this.sectionId} [data-send-inquiry-trigger], #ProductInfo-${this.sectionId} .product-inquiry-btn`
    );
    if (inquiryBtn) {
      inquiryBtn.click();
      return;
    }

    const contactForm = document.querySelector('#ContactForm, [data-contact-form]');
    if (contactForm) {
      this.hide();
      contactForm.scrollIntoView({ behavior: 'smooth' });
    }
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

if (!customElements.get('variant-quantity-drawer')) {
  customElements.define('variant-quantity-drawer', VariantQuantityDrawer);
}
